import React from 'react';
import { bool, object } from 'prop-types';
import classNames from 'classnames';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { injectIntl, intlShape } from '../../util/reactIntl';
import { isScrollingDisabled } from '../../ducks/UI.duck';
import { propTypes } from '../../util/types';
import config from '../../config';
import {
  Page,
  SectionForHirersFeatures,
  SectionForHirersHero,
  SectionForHirersSearch,
  SectionForHirersSteps,
  SectionForHirersTestimonials,
  SectionForHirersTrailerShield,
  LayoutSingleColumn,
  LayoutWrapperTopbar,
  LayoutWrapperMain,
  LayoutWrapperFooter,
  Footer,
} from '../../components';
import { TopbarContainer } from '..';

import facebookImage from '../../assets/lth-og.jpg';
import twitterImage from '../../assets/lth-twitter.jpg';
import css from './ForHirersPage.module.css';

export const ForHirersPageComponent = props => {
  const {
    history,
    intl,
    scrollingDisabled,
    isAuthenticated,
    currentUserListing = null,
    currentUserListingFetched = false,
  } = props;

  // Schema for search engines (helps them to understand what this page is about)
  // http://schema.org
  // We are using JSON-LD format
  const siteTitle = config.siteTitle;
  const schemaTitle = intl.formatMessage({ id: 'ForHirersPage.schemaTitle' }, { siteTitle });
  const schemaDescription = intl.formatMessage(
    { id: 'ForHirersPage.schemaDescription' },
    { siteTitle }
  );
  const schemaImage = `${config.canonicalRootURL}${facebookImage}`;

  return (
    <Page
      className={css.root}
      scrollingDisabled={scrollingDisabled}
      contentType="website"
      description={schemaDescription}
      title={schemaTitle}
      facebookImages={[{ url: facebookImage, width: 1200, height: 630 }]}
      twitterImages={[
        { url: `${config.canonicalRootURL}${twitterImage}`, width: 1024, height: 512 },
      ]}
      schema={{
        '@context': 'http://schema.org',
        '@type': 'WebPage',
        description: schemaDescription,
        name: schemaTitle,
        image: [schemaImage],
      }}
    >
      <LayoutSingleColumn>
        <LayoutWrapperTopbar>
          <TopbarContainer />
        </LayoutWrapperTopbar>
        <LayoutWrapperMain>
          <div className={css.heroContainer}>
            <SectionForHirersHero className={css.hero} />
          </div>
          <ul className={css.sections}>
            <li className={css.section}>
              <div className={css.sectionContentFirstChild}>
                <SectionForHirersFeatures />
              </div>
            </li>
            <li className={classNames(css.section, css.sectionSearch)}>
              <div className={classNames(css.sectionContent, css.sectionSearchContent)}>
                <SectionForHirersSearch history={history} />
              </div>
            </li>
            <li className={css.section}>
              <div className={css.sectionContent}>
                <SectionForHirersSteps />
              </div>
            </li>
            <li className={css.section}>
              <div className={css.sectionContent}>
                <SectionForHirersTestimonials />
              </div>
            </li>
            <li className={css.section}>
              <div className={css.sectionContent}>
                <SectionForHirersTrailerShield />
              </div>
            </li>
          </ul>
        </LayoutWrapperMain>
        <LayoutWrapperFooter>
          <Footer />
        </LayoutWrapperFooter>
      </LayoutSingleColumn>
    </Page>
  );
};

ForHirersPageComponent.propTypes = {
  scrollingDisabled: bool.isRequired,
  currentUserListing: propTypes.ownListing,
  currentUserListingFetched: bool,

  // from withRouter
  history: object.isRequired,

  // from injectIntl
  intl: intlShape.isRequired,
};

const mapStateToProps = state => {
  const { currentUserListing, currentUserListingFetched } = state.user;
  const { isAuthenticated } = state.Auth;

  return {
    scrollingDisabled: isScrollingDisabled(state),
    currentUserListing,
    currentUserListingFetched,
    isAuthenticated,
  };
};

// Note: it is important that the withRouter HOC is **outside** the
// connect HOC, otherwise React Router won't rerender any Route
// components since connect implements a shouldComponentUpdate
// lifecycle hook.
//
// See: https://github.com/ReactTraining/react-router/issues/4671
const ForHirersPage = compose(
  withRouter,
  connect(mapStateToProps),
  injectIntl
)(ForHirersPageComponent);

export default ForHirersPage;
